import React from "react";

import type { ServerDataSuccess } from "./route";

import {
  AnnotatedImageCarousel,
  ShopifyRichText,
  CarouselSection,
  Image,
  ImageModal,
  useModal,
  Zoomable,
  type ImageProps,
} from "@bared/ui";

export interface HowToStyleSectionProps {
  howToStyleImages: ServerDataSuccess["howToStyleImages"];
  howToStyleContent: ServerDataSuccess["howToStyleContent"];
  howToStyleSiblings: ServerDataSuccess["howToStyleSiblings"];
}

export function HowToStyleSection(props: HowToStyleSectionProps) {
  // Join main howToStyleImages with sibling images
  const allImages: ImageProps[] = [
    ...props.howToStyleImages,
    ...((props.howToStyleSiblings.length > 0 &&
      props.howToStyleSiblings.flatMap(
        (sibling: any) => sibling.howToStyleImages,
      )) ||
      []),
  ];

  return (
    <div id="how_to_style_section">
      <MainSection {...props} allImages={allImages} />
      <Modal allImages={allImages} />
    </div>
  );
}

function MainSection({
  allImages,
  howToStyleContent,
  howToStyleSiblings,
}: Pick<HowToStyleSectionProps, "howToStyleContent" | "howToStyleSiblings"> & {
  allImages: ImageProps[];
}) {
  const { showModal } = useModal();

  // Create siblings data for the carousel
  const siblings =
    (howToStyleSiblings.length > 0 &&
      howToStyleSiblings.map((sibling: any) => ({
        handle: sibling.handle,
        colour: sibling.colour,
      }))) ||
    [];

  return (
    <AnnotatedImageCarousel
      ariaLabel="How to style images"
      textContent={
        <div>
          <ShopifyRichText
            className="max-w-lg text-hint"
            content={howToStyleContent as string}
          />
        </div>
      }
      siblings={siblings}
    >
      {allImages.map((image, index) => (
        <CarouselSection
          className="aspect-4-5 [&_img]:object-cover"
          index={index}
          key={`${index}-${image.alt}`}
          onClick={() =>
            showModal("how-to-style-modal", { initialIndex: index })
          }
        >
          <Image {...image} sizes="100vw" loading="eager" aspectRatio="4/5" />
        </CarouselSection>
      ))}
    </AnnotatedImageCarousel>
  );
}

function Modal({ allImages }: { allImages: ImageProps[] }) {
  return (
    <ImageModal id="how-to-style-modal" ariaLabel="How to style images">
      {allImages.map((image, index) => (
        <CarouselSection
          className="aspect-4-5"
          index={index}
          key={`${index}-${image.alt}`}
        >
          <Zoomable>
            <Image {...image} sizes="100vw" loading="eager" aspectRatio="4/5" />
          </Zoomable>
        </CarouselSection>
      ))}
    </ImageModal>
  );
}
